'use client'

import { Checkmark24, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Icon, Image, Navigation, Text } from '@vinted/web-ui'
import { useEffect } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAsset } from '@marketplace-web/shared/assets'

import { kycViewScreenEvent } from '@marketplace-web/pay-identity/pay-identity-kyc-data'

import useFetchBankEducation from '../../../../../hooks/useFetchBankEducation'

type Props = {
  show: boolean
  buttonText?: string
  onUpload: () => void
  onClose: () => void
}

const EducationModal = ({ show, buttonText, onUpload, onClose }: Props) => {
  const { track } = useTracking()
  const educationModalTranslate = useTranslate(
    'kyc_form.supporting_documents.bank_statement.education_modal',
  )
  const translate = useTranslate()
  const asset = useAsset('/assets/kyc')

  const { data: requirements, isLoading } = useFetchBankEducation()

  useEffect(() => {
    if (!show) return

    track(kycViewScreenEvent({ screen: 'kyc_bank_statement_education' }))
  }, [show, track])

  const renderSection = ({ title, subtitle }: { title: string; subtitle?: string }) => (
    <Cell
      key={title}
      styling="narrow"
      title={title}
      body={subtitle && <Text as="h3" html text={subtitle} type="subtitle" />}
      prefix={<Icon name={Checkmark24} />}
    />
  )

  return (
    <Dialog show={show}>
      <Navigation
        right={
          <Button
            styling="flat"
            onClick={onClose}
            iconName={X24}
            testId="kyc_bank_statement_education_close"
            inline
            aria={{
              'aria-label': translate(
                'kyc_form.a11y.supporting_documents.bank_statement.close_button',
              ),
            }}
          />
        }
      />
      <div className="modal-contained-content">
        <div className="u-flexbox u-justify-content-center">
          <Image
            role="img"
            src={asset('bank_statement_education.svg', {
              theme: { dark: 'dark/bank_statement_education.svg' },
            })}
            size="x4-large"
            alt=""
          />
        </div>
        <Cell>
          <Text
            as="h1"
            text={educationModalTranslate('title')}
            type="heading"
            width="parent"
            alignment="center"
          />
        </Cell>
        <div className="u-ui-padding-horizontal-large u-ui-padding-bottom-large">
          <Text
            as="p"
            text={educationModalTranslate('subtitle')}
            type="body"
            width="parent"
            alignment="center"
          />
        </div>
        {isLoading ? <ContentLoader styling={ContentLoader.Styling.Wide} /> : null}
        {requirements?.sections.length ? (
          <>
            <Cell>
              <Text
                as="h2"
                text={educationModalTranslate('must_show')}
                type="title"
                width="parent"
              />
            </Cell>
            {requirements?.sections.map(({ title, subtitle }) =>
              renderSection({
                title,
                subtitle,
              }),
            )}
          </>
        ) : null}
      </div>
      <Divider />
      <Cell>
        <Button
          text={buttonText || educationModalTranslate('buttons.upload')}
          styling="filled"
          onClick={onUpload}
        />
      </Cell>
    </Dialog>
  )
}

export default EducationModal
